import { Form, Formik } from "formik";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import RegisterInput from "../inputs/registerInput";
import * as Yup from "yup";
import DateOfBirthSelect from "./DateOfBirthSelect";
import GenderSelect from "./GenderSelect";
import DotLoader from "react-spinners/DotLoader";
import axios from "axios";
import { uploadImages } from "../../functions/uploadImages";
import { updateprofilePicture } from "../../functions/user";
import { useDispatch } from "react-redux";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";

export default function RegisterForm({ setVisible }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Step state: 0 = enter email, 1 = enter OTP, 2 = registration form
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const otpRefs = useRef([]);
  const profileInputRef = useRef(null);
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleToken, setGoogleToken] = useState(null);
  const [googleScriptReady, setGoogleScriptReady] = useState(false);

  // Poll for Google script (loads async in index.html) so we re-render when ready
  useEffect(() => {
    const check = () => {
      if (window.google?.accounts?.id) {
        setGoogleScriptReady(true);
        return true;
      }
      return false;
    };
    if (check()) return;
    const id = setInterval(() => check() && clearInterval(id), 200);
    const timeout = setTimeout(() => clearInterval(id), 15000);
    return () => {
      clearInterval(id);
      clearTimeout(timeout);
    };
  }, []);

  // Registration info
  const userInfos = {
    first_name: "",
    last_name: "",
    email: email,
    bYear: new Date().getFullYear(),
    bMonth: new Date().getMonth() + 1,
    bDay: new Date().getDate(),
    gender: "",
    privacy: "public",
  };
  const [user, setUser] = useState(userInfos);
  const {
    first_name,
    last_name,
    bYear,
    bMonth,
    bDay,
    gender,
    privacy,
  } = user;
  const yearTemp = new Date().getFullYear();
  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    // Convert date values to numbers for proper validation
    if (name === 'bYear' || name === 'bMonth' || name === 'bDay') {
      setUser({ ...user, [name]: parseInt(value, 10) });
    } else {
      setUser({ ...user, [name]: value });
    }
  };

  const handlePrivacyChange = (e) => {
    setUser({ ...user, privacy: e.target.value });
  };
  const years = Array.from(new Array(108), (val, index) => yearTemp - index);
  const months = Array.from(new Array(12), (val, index) => 1 + index);
  const getDays = () => {
    // Ensure date values are numbers for calculation
    const year = parseInt(bYear, 10) || yearTemp;
    const month = parseInt(bMonth, 10) || 1;
    return new Date(year, month, 0).getDate();
  };
  const days = Array.from(new Array(getDays()), (val, index) => 1 + index);

  // Calculate current age based on date of birth
  const calculateAge = useMemo(() => {
    if (!bYear || !bMonth || !bDay) return null;
    
    // Ensure date values are numbers
    const year = parseInt(bYear, 10);
    const month = parseInt(bMonth, 10);
    const day = parseInt(bDay, 10);
    
    // Check if values are valid numbers
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return null;
    }
    
    const current_date = new Date();
    const picked_date = new Date(year, month - 1, day);
    
    // Validate date is valid
    if (picked_date.getFullYear() !== year || 
        picked_date.getMonth() !== month - 1 || 
        picked_date.getDate() !== day) {
      return null;
    }
    
    // Check if date is in the future
    if (picked_date > current_date) {
      return null;
    }
    
    // Calculate age
    let age = current_date.getFullYear() - year;
    const monthDiff = current_date.getMonth() - (month - 1);
    const dayDiff = current_date.getDate() - day;
    
    // Adjust age if birthday hasn't occurred this year
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }
    
    return age;
  }, [bYear, bMonth, bDay]);

  // Validation for registration form
  const registerValidation = Yup.object({
    first_name: Yup.string()
      .required("What's your First name ?")
      .min(2, "Fisrt name must be between 2 and 16 characters.")
      .max(16, "Fisrt name must be between 2 and 16 characters.")
      .matches(/^[aA-zZ]+$/, "Numbers and special characters are not allowed."),
    last_name: Yup.string()
      .required("What's your Last name ?")
      .min(1, "Last name must be between 1 and 10 characters.")
      .max(10, "Last name must be between 1 and 10 characters.")
      .matches(/^[aA-zZ]+$/, "Numbers and special characters are not allowed."),
  });
  const [dateError, setDateError] = useState("");
  const [genderError, setGenderError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState("");
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Send OTP to email
  const handleSendOtp = async () => {
    setOtpError("");
    setOtpLoading(true);
    try {
      await axios.post(`/api/auth/send-otp`, { email });
      setStep(1);
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to send OTP. Please try again.";
      setOtpError(errorMessage);

      // Show alert for email restriction
      if (err.response?.status === 403) {
        alert("You cant register to this page");
      }
    }
    setOtpLoading(false);
  };

  const otpString = otp.join("");
  // Verify OTP
  const handleVerifyOtp = useCallback(async () => {
    setOtpError("");
    setOtpLoading(true);
    try {
      await axios.post(`/api/auth/verify-otp`, { email, otp: otpString });
      setUser((u) => ({ ...u, email }));
      setStep(2);
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Invalid OTP. Please try again.";
      setOtpError(errorMessage);

      // Show alert for email restriction
      if (err.response?.status === 403) {
        alert("You cant register to this page");
      }
    }
    setOtpLoading(false);
  }, [email, otpString]);

  // Auto-verify OTP when all 4 digits are entered
  useEffect(() => {
    if (otpString.length === 4 && step === 1 && !otpLoading) {
      handleVerifyOtp();
    }
  }, [otpString, step, otpLoading, handleVerifyOtp]);

  // Google Sign-In handler for registration
  const handleGoogleCredential = useCallback(async (response) => {
    try {
      setGoogleLoading(true);
      const token = response.credential;
      
      // Decode JWT to get email (client-side decode just for display)
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));
      
      setGoogleToken(token);
      setEmail(payload.email);
      setStep(2); // Go directly to registration form
      setError("");
    } catch (err) {
      console.error('Google registration error:', err);
      setError(err.response?.data?.message || "Google sign-in failed.");
    } finally {
      setGoogleLoading(false);
    }
  }, []);

  // Initialize Google Sign-In (runs when script is ready)
  useEffect(() => {
    if (!googleScriptReady || !window.google?.accounts?.id) return;
    try {
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
      });
      const div = document.getElementById("googleSignInDivRegister");
      if (div && div.childElementCount === 0) {
        window.google.accounts.id.renderButton(div, {
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "rectangular",
        });
      }
    } catch (e) {
      console.error("Google init error", e);
    }
  }, [handleGoogleCredential, googleScriptReady]);

  // Helper to focus next/prev input
  const handleOtpChange = (e, idx) => {
    const value = e.target.value.replace(/\D/g, "");
    if (!value) return;
    const newOtp = [...otp];
    newOtp[idx] = value[0];
    setOtp(newOtp);
    if (idx < 3 && value) {
      otpRefs.current[idx + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      if (otp[idx]) {
        const newOtp = [...otp];
        newOtp[idx] = "";
        setOtp(newOtp);
      } else if (idx > 0) {
        otpRefs.current[idx - 1]?.focus();
      }
    }
  };

  // Registration submit
  const registerSubmit = async () => {
    try {
      setLoading(true);
      
      // If using Google registration
      if (googleToken) {
        // Ensure date values are numbers before sending to backend
        const { data } = await axios.post(`/api/auth/google-register`, {
          token: googleToken,
          first_name,
          last_name,
          bYear: parseInt(bYear, 10),
          bMonth: parseInt(bMonth, 10),
          bDay: parseInt(bDay, 10),
          gender,
          privacy,
        });
        setError("");
        setSuccess(data.message);
        const { message, ...rest } = data;
        dispatch({ type: "LOGIN", payload: rest });
        Cookies.set("user", JSON.stringify(rest));
        
        // If user selected a custom profile picture, upload it now using the returned token
        if (profilePictureFile) {
          try {
            setUploadingPicture(true);
            const token = rest.token;
            const path = `${rest.username}/profile_pictures`;
            let formData = new FormData();
            formData.append("file", profilePictureFile);
            formData.append("path", path);
            const uploadRes = await uploadImages(formData, path, token);
            if (Array.isArray(uploadRes) && uploadRes.length > 0) {
              const imageData = uploadRes[0];
              const updateRes = await updateprofilePicture(imageData.url, token);
              if (updateRes === "ok") {
                // Update cookie and redux state with new picture
                const updatedUser = { ...rest, picture: imageData.url };
                Cookies.set("user", JSON.stringify(updatedUser));
                dispatch({ type: "UPDATEPICTURE", payload: imageData.url });
              }
            }
          } catch (err) {
            console.error("Error uploading profile picture after Google register:", err);
          } finally {
            setUploadingPicture(false);
          }
        }
        
        setLoading(false);
        setTimeout(() => {
          navigate("/");
        }, 1500);
        return;
      }
      
      // Regular email/OTP registration
      // Ensure date values are numbers before sending to backend
      const { data } = await axios.post(`/register`, {
        first_name,
        last_name,
        email,
        bYear: parseInt(bYear, 10),
        bMonth: parseInt(bMonth, 10),
        bDay: parseInt(bDay, 10),
        gender,
        privacy,
      });
      setError("");
      setSuccess(data.message);
      const { message, ...rest } = data;
      // Automatically log in the user after successful registration
      dispatch({ type: "LOGIN", payload: rest });
      // Note: LOGIN action in reducer will handle saving to cookie and localStorage
      // If user selected a profile picture, upload it now using the returned token
      if (profilePictureFile) {
        try {
          setUploadingPicture(true);
          const token = rest.token;
          const path = `${rest.username}/profile_pictures`;
          let formData = new FormData();
          formData.append("file", profilePictureFile);
          formData.append("path", path);
          const uploadRes = await uploadImages(formData, path, token);
          if (Array.isArray(uploadRes) && uploadRes.length > 0) {
            const imageData = uploadRes[0];
            const updateRes = await updateprofilePicture(imageData.url, token);
            if (updateRes === "ok") {
              // Update redux state with new picture (reducer will handle cookie and localStorage)
              dispatch({ type: "UPDATEPICTURE", payload: imageData.url });
            }
          }
        } catch (err) {
          console.error("Error uploading profile picture after register:", err);
        } finally {
          setUploadingPicture(false);
        }
      }

      setLoading(false);
      // Show success message briefly then redirect
      setTimeout(() => {
        navigate("/");
      }, 1500);
    } catch (error) {
      setLoading(false);
      setSuccess("");
      const errorMessage = error.response?.data?.message || "Registration failed.";
      setError(errorMessage);
      // Show alert for email restriction
      if (error.response?.status === 403) {
        alert("You cant register to this page");
      }
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    // Basic file type/size checks
    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image is too large. Maximum size is 5MB.");
      return;
    }
    setError("");
    setProfilePictureFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setProfilePicturePreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="blur">
      <div className="register">
        <div className="register_header">
          <i className="exit_icon" onClick={() => setVisible(false)}></i>
          <span>Sign Up</span>
          <span>it's quick and easy</span>
        </div>
        {step === 0 && (
          <div className="register_form">
            <input
              type="text"
              placeholder="Enter your email address"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="register_plain_input"
              style={{ width: 300, padding: 10, marginBottom: 10 }}
            />
            <button
              className="blue_btn open_signup"
              onClick={handleSendOtp}
              disabled={otpLoading || !email}
              style={{ marginTop: 16 }}
            >
              {otpLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <DotLoader color="#ffffff" size={16} />
                  <span>Sending OTP...</span>
                </div>
              ) : (
                "Send OTP"
              )}
            </button>
            {otpError && <div className="error_text">{otpError}</div>}
            
            <div className="sign_splitter" style={{ margin: '16px 0' }}></div>
            
            {/* Google Sign-In for Registration */}
            <div className="register_google_wrapper">
              <div id="googleSignInDivRegister"></div>
              {!googleScriptReady && (
                <button type="button" className="blue_btn" disabled>
                  {googleLoading ? null : "Loading Google Sign-In…"}
                </button>
              )}
              {googleLoading && <DotLoader color="#1876f2" loading={true} size={24} />}
            </div>
          </div>
        )}
        {step === 1 && (
          <div className="register_form">
            <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 10 }}>
              {otp.map((digit, idx) => (
                <input
                  key={idx}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleOtpChange(e, idx)}
                  onKeyDown={e => handleOtpKeyDown(e, idx)}
                  ref={el => otpRefs.current[idx] = el}
                  className="register_plain_input"
                  style={{ width: 50, height: 50, textAlign: "center", fontSize: 22 }}
                />
              ))}
            </div>
            {otpError && <div className="error_text">{otpError}</div>}
            <button
              className="blue_btn open_signup"
              onClick={handleSendOtp}
              disabled={otpLoading}
              style={{ marginTop: 8 }}
            >
              {otpLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <DotLoader color="#ffffff" size={16} />
                  <span>Resending...</span>
                </div>
              ) : (
                "Resend OTP"
              )}
            </button>
          </div>
        )}
        {step === 2 && (
          <Formik
            enableReinitialize
            initialValues={{
              first_name,
              last_name,
              bYear,
              bMonth,
              bDay,
              gender,
            }}
            validationSchema={registerValidation}
            onSubmit={() => {
              // Ensure date values are numbers
              const year = parseInt(bYear, 10);
              const month = parseInt(bMonth, 10);
              const day = parseInt(bDay, 10);
              
              // Check if values are valid numbers
              if (isNaN(year) || isNaN(month) || isNaN(day)) {
                setDateError("Please enter a valid date of birth.");
                return;
              }
              
              let current_date = new Date();
              let picked_date = new Date(year, month - 1, day);
              
              // Validate date is valid
              if (picked_date.getFullYear() !== year || picked_date.getMonth() !== month - 1 || picked_date.getDate() !== day) {
                setDateError("Please enter a valid date of birth.");
                return;
              }
              
              // Check if date is in the future
              if (picked_date > current_date) {
                setDateError("Date of birth cannot be in the future.");
                return;
              }
              
              // Calculate age
              let age = current_date.getFullYear() - year;
              const monthDiff = current_date.getMonth() - (month - 1);
              const dayDiff = current_date.getDate() - day;
              
              // Adjust age if birthday hasn't occurred this year
              if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
                age--;
              }
              
              // Validate minimum age of 18
              if (age < 18) {
                setDateError("You must be at least 18 years old to register.");
                return;
              }
              
              // Check maximum reasonable age (e.g., 120 years)
              if (age > 120) {
                setDateError("Please enter a valid date of birth.");
                return;
              }
              
              if (gender === "") {
                setDateError("");
                setGenderError(
                  "Please choose a gender. You can change who can see this later."
                );
              } else {
                setDateError("");
                setGenderError("");
                registerSubmit();
              }
            }}
          >
            {(formik) => (
              <Form className="register_form">
                <div className="reg_line">
                  <RegisterInput
                    type="text"
                    placeholder="First name"
                    name="first_name"
                    onChange={handleRegisterChange}
                  />
                  <RegisterInput
                    type="text"
                    placeholder="Surname"
                    name="last_name"
                    onChange={handleRegisterChange}
                  />
                </div>
                <div className="reg_line">
                  <RegisterInput
                    type="text"
                    placeholder="Email address"
                    name="email"
                    value={email}
                    readOnly
                  />
                </div>
                <div className="reg_col">
                  <div className="reg_line_header" style={{ fontWeight: '650', color: '#050505', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '16px' }}>📅</span>
                    Date of birth
                  </div>
                  <DateOfBirthSelect
                    bDay={bDay}
                    bMonth={bMonth}
                    bYear={bYear}
                    days={days}
                    months={months}
                    years={years}
                    handleRegisterChange={handleRegisterChange}
                    dateError={dateError}
                  />
                  {calculateAge !== null && (
                    <div style={{ 
                      marginTop: '8px', 
                      fontSize: '14px', 
                      color: calculateAge < 18 ? '#e41e3f' : '#65676b',
                      fontWeight: '500'
                    }}>
                      {/* Your age: <strong>{calculateAge} {calculateAge === 1 ? 'year' : 'years'} old</strong>
                      {calculateAge < 18 && (
                        <span style={{ display: 'block', marginTop: '4px', fontSize: '12px' }}>
                          You must be at least 18 years old to register.
                        </span>
                      )} */}
                    </div>
                  )}
                </div>
                <div className="reg_col">
                  <div className="reg_line_header" style={{ fontWeight: '650', color: '#050505', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '16px' }}>👤</span>
                    Gender
                  </div>
                  <GenderSelect
                    handleRegisterChange={handleRegisterChange}
                    genderError={genderError}
                  />
                </div>
                <div className="reg_col">
                  <div className="reg_line_header" style={{ fontWeight: '650', color: '#050505', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '16px' }}>🔒</span>
                    Account Privacy
                  </div>
                  <div style={{ display: 'flex', gap: '20px', margin: '10px 0' }}>
                    <label>
                      <input
                        type="radio"
                        name="privacy"
                        value="public"
                        checked={privacy === "public"}
                        onChange={handlePrivacyChange}
                      />
                      Public
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="privacy"
                        value="private"
                        checked={privacy === "private"}
                        onChange={handlePrivacyChange}
                      />
                      Private
                    </label>
                  </div>
                </div>
                {/* Profile picture selection during registration */}
                <div className="reg_col">
                  <div className="reg_line_header">Profile picture</div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
                    <div>
                      <input
                        ref={profileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleProfilePictureChange}
                        id="register_profile_pic"
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        className="gray_btn"
                        onClick={() => {
                          if (profileInputRef.current) {
                            // Clear previous value so selecting the same file again triggers change
                            profileInputRef.current.value = null;
                            profileInputRef.current.click();
                          }
                        }}
                      >
                        {profilePicturePreview ? 'Change photo' : 'Choose photo'}
                      </button>
                    </div>
                    {profilePicturePreview && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <img src={profilePicturePreview} alt="Preview" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: '50%' }} />
                        <div>
                          <button
                            type="button"
                            aria-label="Remove profile picture"
                            title="Remove profile picture"
                            style={{ cursor: 'pointer', color: '#1876f2', background: 'transparent', border: 'none', padding: 0 }}
                            onClick={() => { setProfilePictureFile(null); setProfilePicturePreview(''); if (profileInputRef.current) profileInputRef.current.value = null; }}
                          >
                            <i className="exit_icon" style={{ fontStyle: 'normal', display: 'inline-block', transform: 'scale(0.9)' }}></i>
                          </button>
                          {uploadingPicture && <div style={{ fontSize: 12, color: '#666' }}>Uploading...</div>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="reg_infos" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '10px' }}>
                  <input
                    type="checkbox"
                    id="agreeToTerms"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    style={{ 
                      marginTop: '2px',
                      cursor: 'pointer',
                      width: '16px',
                      height: '16px',
                      flexShrink: 0
                    }}
                  />
                  <label htmlFor="agreeToTerms" style={{ cursor: 'pointer', fontSize: '11px', color: 'var(--color-secondary)', lineHeight: '1.4' }}>
                    By clicking Sign Up, you agree to our{" "}
                    <span onClick={(e) => { e.stopPropagation(); navigate('/terms'); }}>
                      Terms
                    </span>
                    {", "}
                    <span onClick={(e) => { e.stopPropagation(); navigate('/privacy'); }}>
                      Privacy Policy
                    </span>
                    {/* and <span>Cookie Policy.</span>  */}
                  </label>
                </div>
                <div className="reg_btn_wrapper">
                  <button className="blue_btn open_signup" type="submit" disabled={loading || !agreedToTerms}>
                    {loading ? "Registering..." : "Sign Up"}
                  </button>
                </div>
                <DotLoader color="#1876f2" loading={loading} size={30} />
                {error && <div className="error_text">{error}</div>}
                {success && <div className="success_text">{success}</div>}
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
}
